import { AxiosResponse } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAxiosClient } from "@/hooks";
import {
  GenerateRunSummaryRequest,
  GetRunPersonasRequest,
  GetRunResultsRequest,
} from "@/types/requests";
import {
  DemographicFiltersMap,
  Persona,
  QuestionResults,
  Study,
  Summary,
} from "@/types/resources";
import {
  CreateStudyRunResponse,
  GetRunPersonasResponse,
  GetStudyRunResultsResponse,
} from "@/types/responses";

const denormalizeResults = (
  wireResults: GetStudyRunResultsResponse["results"],
  personas: Record<string, Persona>
): QuestionResults[] =>
  wireResults.map((qr) => ({
    ...qr,
    groups: qr.groups.map((g) => ({
      ...g,
      answers: g.answers.map(({ persona_id, ...rest }) => ({
        ...rest,
        persona: personas[persona_id],
      })),
    })),
  }));

// Helper function to serialize typed filters to API format
const serializeFilters = (
  filters: DemographicFiltersMap
): Record<string, string> => {
  const serialized: Record<string, string> = {};

  Object.entries(filters).forEach(([key, values]) => {
    if (!values || values.length === 0) return;
    serialized[key] = values.join(",");
  });

  return serialized;
};

const useTriggerRun = (studyId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);

  const client = useAxiosClient(`/studies/${studyId}/runs`);

  const triggerRun = useCallback(async () => {
    setError(null);
    setRunId(null);
    setLoading(true);

    try {
      const response = await client.post<
        CreateStudyRunResponse,
        AxiosResponse<CreateStudyRunResponse>,
        Record<string, never>
      >("", {});
      setRunId(response.data.run_id);
      return response.data.run_id;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, [client]);

  return {
    error,
    loading,
    runId,
    triggerRun,
  };
};

const useGetRunResults = (
  studyId: string,
  runId: string,
  params: {
    filters: DemographicFiltersMap;
    groupId?: string;
    itemsPerPage: number;
    page: number;
    questionId?: string;
  }
) => {
  const [count, setCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<QuestionResults[]>([]);
  const [study, setStudy] = useState<Study | null>(null);

  const client = useAxiosClient(`/studies/${studyId}/runs`);

  // Serialize filters for API
  const serializedFilters = useMemo(
    () => serializeFilters(params.filters),
    [params.filters]
  );

  const getResults = useCallback(
    (requestParams: GetRunResultsRequest) => {
      if (!runId) {
        return;
      }

      client
        .get<GetStudyRunResultsResponse>(`/${runId}`, { params: requestParams })
        .then((response) => {
          const {
            count: total,
            personas,
            results: wireResults,
            study: responseStudy,
          } = response.data;
          const denormalized = denormalizeResults(wireResults, personas);
          setCount(total);
          setStudy(responseStudy);
          setResults((prev) =>
            (requestParams.page ?? 1) > 1
              ? [...prev, ...denormalized]
              : denormalized
          );
          setError(null);
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [client, runId]
  );

  // Reset the list during render (not in an effect) when the run or filters
  // change. The initial mount needs no reset since results start empty.
  const resetKey = JSON.stringify([runId, serializedFilters]);
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    setResults([]);
  }

  // Re-enter the loading state during render when any query input changes,
  // mirroring the original fetch (which only set loading past its `!runId`
  // early return).
  const queryKey = JSON.stringify([
    runId,
    serializedFilters,
    params.groupId,
    params.itemsPerPage,
    params.page,
    params.questionId,
  ]);
  const [prevQueryKey, setPrevQueryKey] = useState(queryKey);
  if (prevQueryKey !== queryKey) {
    setPrevQueryKey(queryKey);
    if (runId) {
      setLoading(true);
      setError(null);
    }
  }

  useEffect(() => {
    getResults({
      ...serializedFilters,
      group_id: params.groupId,
      items_per_page: params.itemsPerPage ?? 10,
      page: params.page,
      question_id: params.questionId,
    });
  }, [
    getResults,
    params.groupId,
    params.itemsPerPage,
    params.page,
    params.questionId,
    serializedFilters,
  ]);

  const refresh = useCallback(() => {
    setResults([]);
    if (runId) {
      setLoading(true);
      setError(null);
    }
    getResults({
      ...serializedFilters,
      group_id: params.groupId,
      items_per_page: params.itemsPerPage ?? 10,
      page: 1,
      question_id: params.questionId,
    });
  }, [
    getResults,
    params.groupId,
    params.itemsPerPage,
    params.questionId,
    runId,
    serializedFilters,
  ]);

  return {
    count,
    error,
    loading,
    refresh,
    results,
    study,
  };
};

const useGetRunPersonas = (
  studyId: string,
  runId: string,
  filters?: DemographicFiltersMap
) => {
  const [count, setCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [personas, setPersonas] = useState<Persona[]>([]);

  const client = useAxiosClient(`/studies/${studyId}/runs`);

  // Serialize filters for API
  const serializedFilters = useMemo(
    () => (filters ? serializeFilters(filters) : undefined),
    [filters]
  );

  const getPersonas = useCallback(
    (params: GetRunPersonasRequest) => {
      if (!studyId || !runId) {
        return;
      }

      client
        .get<GetRunPersonasResponse>(`/${runId}/personas`, { params })
        .then((response) => {
          setCount(response.data.count);
          setPersonas(response.data.personas);
          setError(null);
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [client, runId, studyId]
  );

  // Re-enter the loading state during render when the inputs change, mirroring
  // the original fetch (which only set loading past its early return).
  const queryKey = JSON.stringify([studyId, runId, serializedFilters]);
  const [prevQueryKey, setPrevQueryKey] = useState(queryKey);
  if (prevQueryKey !== queryKey) {
    setPrevQueryKey(queryKey);
    if (studyId && runId) {
      setLoading(true);
      setError(null);
    }
  }

  useEffect(() => {
    getPersonas(serializedFilters ?? {});
  }, [serializedFilters, getPersonas]);

  const refresh = useCallback(() => {
    if (studyId && runId) {
      setLoading(true);
      setError(null);
    }
    getPersonas(serializedFilters ?? {});
  }, [getPersonas, runId, serializedFilters, studyId]);

  return {
    count,
    error,
    loading,
    personas,
    refresh,
  };
};

const useGenerateRunSummary = (studyId: string, runId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);

  const client = useAxiosClient(`/studies/${studyId}/runs`);

  const generateSummary = useCallback(
    async (body: GenerateRunSummaryRequest) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.post<
          Summary,
          AxiosResponse<Summary>,
          GenerateRunSummaryRequest
        >(`/${runId}/summary`, body);
        setSummary(response.data);
        return response.data;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client, runId]
  );

  return {
    error,
    generateSummary,
    loading,
    summary,
  };
};

export {
  useGenerateRunSummary,
  useGetRunPersonas,
  useGetRunResults,
  useTriggerRun,
};
